/**
 * Screen Reader Utility
 * Uses Web Speech API to read text content
 */

/**
 * Get Arabic voice from available voices
 * Tries to find any Arabic voice, with preference for Saudi Arabic
 */
function getArabicVoice(synth: SpeechSynthesis): SpeechSynthesisVoice | null {
  const voices = synth.getVoices()
  
  if (voices.length === 0) {
    console.warn('No voices available')
    return null
  }
  
  console.log('All available voices:', voices.map(v => ({ name: v.name, lang: v.lang, localService: v.localService })))
  
  // Try multiple patterns to find Arabic voice - more comprehensive search
  const arabicVoices = voices.filter(voice => {
    const lang = voice.lang.toLowerCase()
    const name = voice.name.toLowerCase()
    return (
      lang.startsWith('ar') || 
      lang.includes('arabic') ||
      lang.includes('ar-') ||
      name.includes('arabic') ||
      name.includes('عربي') ||
      name.includes('arab') ||
      // Try to match common Arabic voice names
      name.includes('zira') || // Sometimes Arabic voices have unusual names
      name.includes('hazim') || // Common Arabic TTS voice
      name.includes('naayf') || // Common Arabic TTS voice
      name.includes('salim') || // Common Arabic TTS voice
      // Check for Arabic language codes
      lang === 'ar' ||
      lang.startsWith('ar-')
    )
  })
  
  console.log('Arabic voices found:', arabicVoices.map(v => ({ name: v.name, lang: v.lang })))
  
  // Prefer Saudi Arabic (ar-SA) if available
  const saudiVoice = arabicVoices.find(v => {
    const lang = v.lang.toLowerCase()
    return (
      lang.includes('sa') || 
      lang === 'ar-sa' ||
      lang === 'ar-SA' ||
      v.name.toLowerCase().includes('saudi') ||
      v.name.toLowerCase().includes('السعودية')
    )
  })
  if (saudiVoice) {
    console.log('✅ Using Saudi Arabic voice:', saudiVoice.name, saudiVoice.lang)
    return saudiVoice
  }
  
  // Try other Arabic variants (Egypt, UAE, etc.)
  const otherArabicVoices = arabicVoices.filter(v => {
    const lang = v.lang.toLowerCase()
    return (
      lang === 'ar-eg' || // Egyptian Arabic
      lang === 'ar-ae' || // UAE Arabic
      lang === 'ar-jo' || // Jordanian Arabic
      lang === 'ar-lb' || // Lebanese Arabic
      lang === 'ar-ma' || // Moroccan Arabic
      lang === 'ar-dz' || // Algerian Arabic
      lang === 'ar-tn' || // Tunisian Arabic
      lang.startsWith('ar-')
    )
  })
  
  if (otherArabicVoices.length > 0) {
    console.log('✅ Using Arabic voice (variant):', otherArabicVoices[0].name, otherArabicVoices[0].lang)
    return otherArabicVoices[0]
  }
  
  // Return first Arabic voice found (any Arabic voice is better than none)
  if (arabicVoices.length > 0) {
    console.log('✅ Using Arabic voice (any):', arabicVoices[0].name, arabicVoices[0].lang)
    return arabicVoices[0]
  }
  
  console.error('❌ No Arabic voice found!')
  console.error('💡 To fix this issue:')
  console.error('   1. Open Windows Settings')
  console.error('   2. Go to Time & Language > Speech')
  console.error('   3. Click "Add voices"')
  console.error('   4. Install "Arabic (Saudi Arabia)" or any Arabic voice')
  console.error('   5. Restart your browser')
  console.error('📋 Available voices:', voices.map(v => v.name + ' (' + v.lang + ')').join(', '))
  return null
}

class ScreenReader {
  private synth: SpeechSynthesis | null = null
  private utterance: SpeechSynthesisUtterance | null = null
  private isEnabled: boolean = false
  private isReading: boolean = false
  private currentText: string = ''
  private hoverTimeout: NodeJS.Timeout | null = null
  private arabicVoice: SpeechSynthesisVoice | null = null
  
  // Expose synth for checking paused state
  get synthInstance() {
    return this.synth
  }

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synth = window.speechSynthesis
      
      // Load voices when available
      const loadVoices = () => {
        if (this.synth) {
          this.arabicVoice = getArabicVoice(this.synth)
          console.log('Available voices:', this.synth.getVoices().map(v => ({ name: v.name, lang: v.lang })))
          console.log('Selected Arabic voice:', this.arabicVoice ? { name: this.arabicVoice.name, lang: this.arabicVoice.lang } : 'None found')
        }
      }
      
      // Voices might be loaded asynchronously
      if (this.synth.getVoices().length > 0) {
        loadVoices()
      } else {
        this.synth.onvoiceschanged = loadVoices
      }
    }
  }

  /**
   * Check if screen reader is supported
   */
  isSupported(): boolean {
    return this.synth !== null
  }

  /**
   * Enable/Disable screen reader
   */
  setEnabled(enabled: boolean) {
    if (enabled === this.isEnabled) {
      return
    }
    
    this.isEnabled = enabled
    
    if (!enabled) {
      this.stop()
      this.cancelHoverRead()
    } else {
      // Reload voices when enabling
      if (this.synth) {
        this.arabicVoice = getArabicVoice(this.synth)
      }
      this.isReading = false
      this.utterance = null
    }
  }

  /**
   * Check if screen reader is enabled
   */
  getEnabled(): boolean {
    return this.isEnabled
  }

  /**
   * Check if currently reading
   */
  getIsReading(): boolean {
    return this.isReading
  }

  /**
   * Read text content - always uses Arabic
   */
  read(text: string, options?: {
    lang?: string
    rate?: number
    pitch?: number
    volume?: number
  }) {
    if (!this.synth || !this.isEnabled || !text.trim()) {
      return
    }

    // Stop any current reading
    if (this.synth.speaking || this.synth.pending) {
      this.synth.cancel()
    }
    
    // Clear any pending utterances
    this.utterance = null
    this.isReading = false

    // Wait a bit to ensure previous speech is stopped
    setTimeout(() => {
      if (!this.isEnabled || !this.synth) return

      // Create new utterance
      this.currentText = text
      this.utterance = new SpeechSynthesisUtterance(text)

      // Try to get Arabic voice - reload voices if needed
      // Force reload voices to ensure we have the latest list
      if (this.synth.getVoices().length === 0) {
        // Wait for voices to load
        this.synth.onvoiceschanged = () => {
          this.arabicVoice = getArabicVoice(this.synth!)
        }
      } else {
        this.arabicVoice = getArabicVoice(this.synth)
      }
      
      // If still no voice, try one more time with all available voices
      if (!this.arabicVoice) {
        const allVoices = this.synth.getVoices()
        // More aggressive search for any Arabic voice
        this.arabicVoice = allVoices.find(v => {
          const lang = v.lang.toLowerCase()
          const name = v.name.toLowerCase()
          return (
            lang.startsWith('ar') ||
            lang.includes('arabic') ||
            name.includes('arabic') ||
            name.includes('عربي') ||
            name.includes('arab') ||
            lang === 'ar' ||
            lang.startsWith('ar-')
          )
        }) || null
      }
      
      // Set Arabic voice and language
      if (this.arabicVoice) {
        this.utterance.voice = this.arabicVoice
        // Use the voice's language for better compatibility
        this.utterance.lang = this.arabicVoice.lang || 'ar-SA'
        console.log('✅ Using Arabic voice:', this.arabicVoice.name, 'with lang:', this.utterance.lang)
      } else {
        // No Arabic voice found - try to use any voice with Arabic lang (one more attempt)
        const voices = this.synth.getVoices()
        const anyArabicVoice = voices.find(v => {
          const lang = v.lang.toLowerCase()
          return lang.startsWith('ar') || lang.includes('arabic')
        })
        
        if (anyArabicVoice) {
          this.utterance.voice = anyArabicVoice
          this.utterance.lang = anyArabicVoice.lang
          console.log('✅ Using fallback Arabic voice:', anyArabicVoice.name, 'with lang:', anyArabicVoice.lang)
        } else {
          // Force Arabic language even without voice
          // Note: This will NOT work - browser will use default English voice
          this.utterance.lang = 'ar-SA'
          console.error('❌ CRITICAL: No Arabic voice available!')
          console.error('⚠️ Text will be read in ENGLISH because no Arabic TTS voice is installed.')
          console.error('💡 SOLUTION: Install Arabic voice in Windows Settings > Time & Language > Speech')
          console.error('📋 Current available voices:', voices.map(v => `${v.name} (${v.lang})`).join(', '))
          
          // Show alert to user (only once)
          if (!(window as any).__arabicVoiceWarningShown) {
            (window as any).__arabicVoiceWarningShown = true
            setTimeout(() => {
              alert('⚠️ تحذير: لا يوجد صوت عربي مثبت في النظام.\n\n' +
                    'سيتم قراءة النص بالإنجليزية.\n\n' +
                    'للحل:\n' +
                    '1. افتح إعدادات Windows\n' +
                    '2. اذهب إلى Time & Language > Speech\n' +
                    '3. اضغط "Add voices"\n' +
                    '4. ثبت "Arabic (Saudi Arabia)" أو أي صوت عربي آخر\n' +
                    '5. أعد تشغيل المتصفح')
            }, 1000)
          }
        }
      }

      // Set speech parameters
      this.utterance.rate = options?.rate ?? 0.9 // Slightly slower for Arabic
      this.utterance.pitch = options?.pitch ?? 1.0
      this.utterance.volume = options?.volume ?? 1.0

      // Event handlers
      this.utterance.onstart = () => {
        this.isReading = true
        console.log('Started reading:', text.substring(0, 50), '...', 'Voice:', this.utterance?.voice?.name, 'Lang:', this.utterance?.lang)
      }

      this.utterance.onend = () => {
        this.isReading = false
        this.utterance = null
      }

      this.utterance.onerror = (error) => {
        const expectedErrors = ['interrupted', 'canceled', 'network', 'synthesis-failed']
        
        if (!expectedErrors.includes(error.error)) {
          console.warn('Screen reader error:', error.error, error)
        }
        
        this.isReading = false
        this.utterance = null
      }

      // Speak
      try {
        if (this.synth && 'speak' in this.synth && this.utterance) {
          this.synth.speak(this.utterance)
        }
      } catch (error) {
        console.error('Failed to speak:', error)
        this.isReading = false
        this.utterance = null
      }
    }, 100)
  }

  /**
   * Stop reading
   */
  stop() {
    if (this.synth) {
      this.synth.cancel()
      this.isReading = false
      this.utterance = null
    }
  }

  /**
   * Pause reading
   */
  pause() {
    if (this.synth && this.isReading && this.synth.speaking) {
      this.synth.pause()
    }
  }

  /**
   * Resume reading
   */
  resume() {
    if (this.synth && this.synth.paused) {
      this.synth.resume()
      this.isReading = true
    }
  }

  /**
   * Read element content - reads what you hover on
   */
  readElement(element: HTMLElement | null, immediate: boolean = true) {
    if (!element || !this.isEnabled) return

    // Get accessible text
    let text = ''
    
    if (element.getAttribute('aria-label')) {
      text = element.getAttribute('aria-label') || ''
    } else if (element.getAttribute('title')) {
      text = element.getAttribute('title') || ''
    } else if (element.getAttribute('alt')) {
      text = element.getAttribute('alt') || ''
    } else {
      // Get all text content from element
      const clone = element.cloneNode(true) as HTMLElement
      
      // Remove unwanted elements
      const unwanted = clone.querySelectorAll('script, style, noscript, audio, video, iframe, [data-screen-reader-ignore="true"]')
      unwanted.forEach(el => el.remove())
      
      text = clone.innerText || clone.textContent || ''
    }
    
    const cleanText = text.trim()

    // Read if there's text
    if (cleanText && cleanText.length >= 1) {
      if (immediate) {
        this.read(cleanText)
      } else {
        // Cancel previous hover timeout
        if (this.hoverTimeout) {
          clearTimeout(this.hoverTimeout)
        }
        // Read after short delay
        this.hoverTimeout = setTimeout(() => {
          if (this.isEnabled) {
            this.read(cleanText)
          }
        }, 200) // 200ms delay
      }
    }
  }

  /**
   * Cancel hover reading
   */
  cancelHoverRead() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
      this.hoverTimeout = null
    }
  }

  /**
   * Read page title
   */
  readPageTitle() {
    const title = document.title || ''
    if (title) {
      this.read(title)
    }
  }

  /**
   * Read main content area
   */
  readMainContent() {
    const main = document.querySelector('main') || document.querySelector('[role="main"]')
    if (main) {
      const clone = main.cloneNode(true) as HTMLElement
      const unwanted = clone.querySelectorAll('script, style, noscript, audio, video, iframe, [data-screen-reader-ignore="true"], .accessibility-toolbar')
      unwanted.forEach(el => el.remove())
      const text = clone.innerText || clone.textContent || ''
      const cleanText = text.trim()
      
      if (cleanText && cleanText.length > 0) {
        this.read(cleanText)
      }
    } else {
      const body = document.body
      if (body) {
        const clone = body.cloneNode(true) as HTMLElement
        const unwanted = clone.querySelectorAll('script, style, noscript, audio, video, iframe, header, footer, nav, [data-screen-reader-ignore="true"]')
        unwanted.forEach(el => el.remove())
        const text = clone.innerText || clone.textContent || ''
        const cleanText = text.trim()
        if (cleanText && cleanText.length > 0) {
          this.read(cleanText)
        }
      }
    }
  }

  /**
   * Read focused element
   */
  readFocusedElement() {
    const focused = document.activeElement as HTMLElement
    if (focused && focused !== document.body) {
      this.readElement(focused, true)
    }
  }
}

// Export singleton instance
export const screenReader = new ScreenReader()
